/*
 * AiVisibilityCheck
 * The reader enters a practice area and a city. The tool builds four questions to ask ChatGPT,
 * Perplexity or Google, and the reader records what happened for each one.
 *
 * Rules
 *   Questions appear only when both fields have text (trimmed).
 *   For each question the reader picks: "Named and linked" | "Named, no link" | "Not named".
 *   Result shows once all four are marked:
 *     named = questions marked "Named and linked" or "Named, no link"
 *     linked = questions marked "Named and linked"
 *     named 0      -> "AI tools aren't naming your firm yet"
 *     named 1 to 2 -> "You're named for some questions, not others"
 *     named 3 to 4 -> "AI tools already know your firm" (if linked < named, add a note about missing links)
 *   Copy buttons use the clipboard only when the browser allows it; nothing else leaves the page.
 * Answers vary by tool, user and day, so the result is a snapshot, not a score. No tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";

type Mark = "linked" | "named" | "none";
const MARKS: { value: Mark; label: string }[] = [
  { value: "linked", label: "Named and linked" },
  { value: "named", label: "Named, no link" },
  { value: "none", label: "Not named" },
];

export default function AiVisibilityCheck({ ctaHref = "#free-plan" }: { ctaHref?: string }) {
  const uid = useId();
  const [practice, setPractice] = useState("");
  const [city, setCity] = useState("");
  const [marks, setMarks] = useState<Record<number, Mark | undefined>>({});
  const [copied, setCopied] = useState<number | null>(null);

  const p = practice.trim();
  const c = city.trim();
  const ready = p.length > 0 && c.length > 0;
  const questions = ready
    ? [
        `Who is the best ${p} lawyer in ${c}?`,
        `I need a ${p} lawyer in ${c}. Who should I call?`,
        `Which law firms in ${c} handle ${p} cases?`,
        `How do I choose a ${p} lawyer in ${c}?`,
      ]
    : [];

  const markedCount = questions.filter((_, i) => marks[i]).length;
  const done = ready && markedCount === 4;
  const named = questions.filter((_, i) => marks[i] === "linked" || marks[i] === "named").length;
  const linked = questions.filter((_, i) => marks[i] === "linked").length;

  let result: { title: string; body: string } | null = null;
  if (done) {
    if (named === 0)
      result = { title: "AI tools aren't naming your firm yet.", body: "Start with the basics: clear practice pages, consistent details across the web and more reviews and mentions on trusted sites." };
    else if (named <= 2)
      result = { title: "You're named for some questions, not others.", body: "AI tools know your firm but aren't confident about it. Clearer answers on your practice pages and more consistent details usually close the gap." };
    else
      result = {
        title: "AI tools already know your firm.",
        body: linked < named ? "Some answers name you without linking to your site, so check that your pages are easy to crawl and clearly answer the question." : "Keep checking that the answers describe your firm accurately, and test more questions and practice areas.",
      };
  }

  const copy = async (text: string, i: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(i);
    } catch {
      setCopied(null);
    }
  };

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">AI visibility check</h3>
      <p className="mt-1 text-sm text-muted-foreground">Nothing you enter is saved or sent anywhere.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${uid}-practice`} className="block text-sm font-medium">Main practice area</label>
          <input id={`${uid}-practice`} type="text" autoComplete="off" placeholder="e.g. car accident" value={practice}
            onChange={(e) => { setPractice(e.target.value); setMarks({}); }}
            className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`} />
        </div>
        <div>
          <label htmlFor={`${uid}-city`} className="block text-sm font-medium">City</label>
          <input id={`${uid}-city`} type="text" autoComplete="off" placeholder="e.g. Springfield" value={city}
            onChange={(e) => { setCity(e.target.value); setMarks({}); }}
            className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`} />
        </div>
      </div>

      {ready && (
        <ol className="mt-5 space-y-3">
          {questions.map((q, i) => {
            const labelId = `${uid}-q${i}`;
            return (
              <li key={i} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p id={labelId} className="text-sm font-medium">{i + 1}. {q}</p>
                  <Button variant="outline" size="sm" type="button" onClick={() => copy(q, i)}
                    className={`rounded-md border border-input px-3 py-1 text-xs ${focusRing}`}>
                    {copied === i ? "Copied" : "Copy"}
                  </Button>
                </div>
                <div role="group" aria-labelledby={labelId} className="mt-3 flex flex-wrap gap-2">
                  {MARKS.map((m) => {
                    const pressed = marks[i] === m.value;
                    return (
                      <Button variant={pressed ? "default" : "outline"} size="sm" key={m.value} type="button" aria-pressed={pressed}
                        onClick={() => setMarks((s) => ({ ...s, [i]: m.value }))}
                        className={`rounded-md border px-3 py-1.5 text-sm ${focusRing} ${
                          pressed ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
                        }`}>
                        {m.label}
                      </Button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!ready ? (
          <p className="text-muted-foreground">Enter a practice area and a city to get your four questions.</p>
        ) : !result ? (
          <p className="text-muted-foreground">
            Ask each question in ChatGPT, Perplexity or Google, then mark what happened. {4 - markedCount} left.
          </p>
        ) : (
          <div>
            <p className="font-semibold">{result.title}</p>
            <p className="mt-1">{result.body}</p>
            <p className="mt-2 text-muted-foreground">Named in {named} of 4 answers, linked in {linked}. AI answers change by tool, user and day, so treat this as a snapshot.</p>
            <p className="mt-4">
              Want the full picture across every AI tool?{" "}
              <a href={ctaHref} className={`font-medium underline underline-offset-4 ${focusRing}`}>{ctaHref === "/law-firm-seo-audit" ? "Get a free AI visibility audit →" : "Get a free AI visibility review →"}</a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
